// VENDORED from three.js r186: examples/jsm/objects/GaussianSplat.js
//
// SEVEN behavioural changes from three r186. Every site that differs carries a
// `PATCHED (aos)` marker, so a change spans several of them — grep the marker to
// find the edits, read this list to know what they add up to:
//
//   1. the blur's alpha attenuation is OPT-IN, per instance, and floored
//   2. the kernel cutoff is a named constant a path can set
//   3. the sort metric is radial (sparkjs's), not upstream's planar
//   4. a splat's colour is taken as display-referred
//   5. `setShRotation` — the frame the spherical harmonics were fitted in
//   6. an opt-in `cameraSeeThrough` fade
//   7. a `dispose()` — upstream defines none, and three call sites believed it did
// The first of them, and the reason the fork exists at all: the alpha attenuation
// that compensates for the 2D blur is removed (see `alphaScale` below)
// and defaults OFF. Everything else is upstream, byte for byte, so re-syncing on a
// three upgrade is: copy the new file, redo the two relative imports (they become
// bare `three/examples/jsm/...` specifiers outside three's own tree), and re-apply
// that one branch.
//
// Vendored rather than forked: examples/jsm is example code meant to be adapted,
// the material builders that would let us override this are module-private, and
// zeroing KERNEL_2D_SIZE would drop the dilation we want to keep along with the
// attenuation we do not.

import {
	Box3,
	BufferAttribute,
	InstancedBufferGeometry,
	Matrix3,
	Matrix4,
	Mesh,
	NodeMaterial,
	Ray,
	Sphere,
	StorageBufferAttribute,
	Vector2,
	Vector3
} from 'three/webgpu';

import {
	Discard,
	Fn,
	If,
	atan,
	cameraProjectionMatrix,
	cameraViewport,
	cos,
	dot,
	exp,
	float,
	highpModelViewMatrix,
	instanceIndex,
	max,
	min,
	mix,
	normalize,
	positionGeometry,
	pow,
	sin,
	smoothstep,
	sqrt,
	step,
	storage,
	uint,
	uniform,
	sRGBTransferEOTF,
	unpackUnorm4x8,
	varyingProperty,
	vec2,
	vec3,
	vec4
} from 'three/tsl';

import { CountingSort } from 'three/examples/jsm/gpgpu/CountingSort.js';
import {
	SH_BAND_COMPONENTS,
	SH_BAND_WORDS,
	getSphericalHarmonicsDegree
} from 'three/examples/jsm/utils/GaussianSplatUtils.js';

const BIN_COUNT = 4096;
const WORKGROUP_SIZE = 256;
/** The ONE reader: the bin count, the bin key and the CPU twin all come through
 *  here, so they cannot disagree. They used to — the count was overridable while
 *  both keys mapped depth onto the module constant, so no key above 4095 was ever
 *  produced and the extra bins stood empty. Raising it was the theory behind the
 *  patchy tile seams; measured, it was not the cause (the arrival-order scatter
 *  was — see threeStableSort.js). */
function binCount() { return BIN_COUNT; }
const SORT_DIRECTION_THRESHOLD = 0.9995;
const KERNEL_2D_SIZE = 0.3;
// PATCHED (aos): a KNOB. three truncates each Gaussian at 2 sigma; sparkjs and our
// own raster use sqrt(8) ~ 2.83. Environment splats average 0.33 opacity and rely on
// many overlapping tails to compose, so cutting the tails earlier leaves thin dark
// features (tile grout) under-filled and patchy.
const SPLAT_KERNEL_CUTOFF_DEFAULT = 2;
/** The quad half-extent in sigma. The geometry, the bounding volumes and the
 *  fragment's own r2 test must all agree on it, or a splat is clipped by
 *  whichever is tighter. */
function quadCutoff() {
	return GaussianSplat.kernelCutoff || SPLAT_KERNEL_CUTOFF_DEFAULT;
}
// PATCHED (aos): THE KERNEL EXTENT IS THE ONLY TERM THE TWO RENDERERS DIFFER ON,
// and sparkjs's is sqrt(8) = 2.83 sigma against upstream three's 2.
//
// Measured on ONE splat rather than on a room: drawn on black, same camera,
// falloff switched off on BOTH sides,
// the two agree on centre to 0.15 px, on angle to 0.4 deg and on
// aspect to 4% — and sparkjs's footprint is a flat 1.42x larger. Set three's
// cutoff to 2.83 and the footprints match to 0.5% flat and 0.1% with the real
// Gaussian. That is the whole geometric difference; a float64 term-by-term
// comparison of the two pipelines said the same from the other end, with cov3D,
// cov2D and both eigenvalues agreeing EXACTLY.
//
// TWO THINGS READ OUT OF SPARKJS'S SHADER SOURCE ARE NOT ON ITS PATH, and both
// were ported here before being checked. `rgba.a *= 2.0` and the solid-splat
// branch above alpha 1 (a remap to min(4a-3,5) plus a kernel widened to 5.63
// sigma and a hard-disc falloff) are real lines in `splatVertex_default`, and
// neither runs: sweeping a splat's opacity from 0.1 to 0.9 moves sparkjs's drawn
// extent by 0.0% where the branch predicts 80% growth, and at opacity 0.3 on
// black sparkjs peaks at 75 where doubling predicts 153 and no doubling predicts
// 76.5. Reproducing them made the room measurably worse. Do not re-derive them
// from the source without measuring that they fire.
//
// Also measured, against the live material rather than assumed: sparkjs runs
// `maxPixelRadius` 512 (it caps the FINAL half-extent, where three caps sigma and
// multiplies after), `minAlpha` 0.00196, and in THIS APP `preBlurAmount` 0.3 with
// `blurAmount` 0 — so the 0.3 is inside both determinants and its `blurAdjust` is
// exactly 1. three's `alphaAttenuation` default of OFF already matches it.
const COVARIANCE_FLATNESS = 1e-4;
const MIN_RAYCAST_OPACITY = 0.2;
// SORT METRIC. Upstream orders splats by PLANAR view depth (-viewZ); sparkjs
// orders by RADIAL distance from the camera (`sortRadial` defaults true there).
// The two diverge off-axis -- at equal planar depth, the splat further from the
// view axis is further away radially -- so they composite overlapping splats in a
// different order. Measured on Startup_Level against Spark: under planar, three's
// dark pixels came out +12.8 and +14.7 levels too BRIGHT (far splats painting over
// near ones, washing thin dark features out); under radial the same buckets land
// at +0.7 and -0.6, and the wall's luminance sd matches Spark's to 0.02 (23.46 vs
// 23.44) where planar gave 21.63. So this defaults to sparkjs's convention, not
// upstream's.
//
// PATCHED (aos): NO URL FLAG. Upstream's planar order is reachable from a
// console or a spec as `GaussianSplat.sortRadial = false`, which is the class
// static every instance already reads — a flag would be a second name for it,
// and this comment promised one that never existed.
const SPLAT_SORT_RADIAL_DEFAULT = true;

// Upstream's cap, on SIGMA, so the drawn half-extent reaches kernelCutoff x this.
// Capping it to sparkjs's tighter half-extent rule was measured and changes nothing
// on these scenes (no large floaters), so upstream's value stands.
const MAX_SCREEN_SPACE_SPLAT_SIZE = 1024;
const CLIP_XY = 1.4;

const _worldCenter = /*@__PURE__*/ new Vector3();
const _viewCenter = /*@__PURE__*/ new Vector3();
const _worldScale = /*@__PURE__*/ new Vector3();
const _sortDirection = /*@__PURE__*/ new Vector3();
const _sortDepthRange = /*@__PURE__*/ new Vector2();
const _worldMatrixInverse = /*@__PURE__*/ new Matrix4();
const _modelViewMatrix = /*@__PURE__*/ new Matrix4();
const _inverseMatrix = /*@__PURE__*/ new Matrix4();
const _ray = /*@__PURE__*/ new Ray();
const _sphere = /*@__PURE__*/ new Sphere();
const _covarianceMatrix = /*@__PURE__*/ new Matrix3();
const _originOffset = /*@__PURE__*/ new Vector3();
const _mDirection = /*@__PURE__*/ new Vector3();
const _mOriginOffset = /*@__PURE__*/ new Vector3();
const _vector = /*@__PURE__*/ new Vector3();

/**
 * A minimal renderer for 3D Gaussian splat geometry.
 *
 * Note that this class can only be used with {@link WebGPURenderer}. The
 * `forceWebGL` fallback of {@link WebGPURenderer} is supported, but
 * {@link WebGLRenderer} is not. Import maps or package exports must resolve
 * both `three/webgpu` and `three/tsl`.
 *
 * ```js
 * const splats = new GaussianSplat( geometry );
 * scene.add( splats );
 * ```
 *
 * @augments Mesh
 * @three_import import { GaussianSplat } from 'three/addons/objects/GaussianSplat.js';
 */
class GaussianSplat extends Mesh {

	/**
	 * Constructs a new Gaussian splat mesh.
	 *
	 * @param {BufferGeometry} splatGeometry - The splat geometry to render. Higher-order spherical harmonics attributes must use packed `Uint32Array` words from {@link createGaussianSplatGeometry} (`SH_BAND_WORDS[ degree ]` words per splat, four clamped-byte coefficients per word).
	 * @param {Object} [options] - Options.
	 * @param {boolean} [options.autoSort=true] - Whether to sort automatically in `onBeforeRender`.
	 * @param {boolean} [options.alphaAttenuation] - PATCHED (aos): per-INSTANCE, see below.
	 * @param {number} [options.alphaAttenuationFloor] - PATCHED (aos): per-INSTANCE.
	 */
	constructor( splatGeometry, {
		autoSort = true,
		seeThrough = false,
		// PATCHED (aos): PER-INSTANCE, because the two consumers want OPPOSITE
		// answers and BOTH MESHES CAN BE ALIVE AT ONCE — a splat character whose
		// bridge hands the environment back late leaves the avatar's mesh and the
		// room's mesh in the same frame, wanting false and true respectively.
		//
		// These were class statics, read here at material-BUILD time, so which mesh
		// got which value depended on construction ORDER. It happened to work, and
		// the bridge carried a re-assert-everything-at-build workaround to keep it
		// working. An option cannot be got wrong by ordering.
		//
		// The statics remain as the DEFAULTS so a caller that sets neither behaves
		// exactly as before.
		alphaAttenuation = GaussianSplat.alphaAttenuation,
		alphaAttenuationFloor = GaussianSplat.alphaAttenuationFloor,
	} = {} ) {

		const positionAttribute = splatGeometry.getAttribute( 'position' );
		const covarianceAttribute = splatGeometry.getAttribute( 'covariance' );
		const colorAttribute = splatGeometry.getAttribute( 'color' );
		const sphericalHarmonicsDegree = getSphericalHarmonicsDegree( splatGeometry );
		const count = positionAttribute.count;

		if ( splatGeometry.boundingBox === null ) splatGeometry.computeBoundingBox();
		if ( splatGeometry.boundingSphere === null ) splatGeometry.computeBoundingSphere();

		const geometry = createGeometry( count );
		const buffers = createStorageBuffers( count, positionAttribute.array, covarianceAttribute.array, colorAttribute.array, {
			degree: sphericalHarmonicsDegree,
			sh1: sphericalHarmonicsDegree >= 1 ? splatGeometry.getAttribute( 'sphericalHarmonics1' ).array : undefined,
			sh2: sphericalHarmonicsDegree >= 2 ? splatGeometry.getAttribute( 'sphericalHarmonics2' ).array : undefined,
			sh3: sphericalHarmonicsDegree >= 3 ? splatGeometry.getAttribute( 'sphericalHarmonics3' ).array : undefined
		} );
		const localCameraPosition = uniform( new Vector3() );
		// PATCHED (aos): see `setShRotation` and the note in applySphericalHarmonics.
		const shRotation = uniform( new Matrix3() );
		// PATCHED (aos): opt-in cameraSeeThrough. Null unless asked for, and then
		// three uniforms whose defaults (strength 0) leave every alpha untouched —
		// so a splat built without it, or with it and never driven, renders exactly
		// as upstream does.
		const seeThroughUniforms = seeThrough === true ? {
			a: uniform( new Vector3() ),            // camera, splat-local space
			b: uniform( new Vector3() ),            // head, splat-local space
			k: uniform( new Vector3( 1, 1, 0 ) ),   // radius, fade, strength
			// FIRST SLOT THE FADE MAY TOUCH. A mesh that is entirely environment
			// leaves it at 0 and every splat is a candidate. The avatar's mesh holds
			// the face and the room in ONE buffer and sets it to the first
			// environment slot — without it the fade, which is centred on the head,
			// dissolves the face it exists to reveal.
			slot0: uniform( 0 )
		} : null;
		const sphericalHarmonicsComputeNode = createSphericalHarmonicsComputeNode( buffers, localCameraPosition, shRotation );
		const sort = new CountingSort( count, { binCount: binCount(), workgroupSize: WORKGROUP_SIZE } );
		const materialNodes = createMaterialNodes( buffers, sort, localCameraPosition, shRotation, seeThroughUniforms,
			// PATCHED (aos): latched per instance, not read off the class.
			{ alphaAttenuation: alphaAttenuation === true, alphaAttenuationFloor: alphaAttenuationFloor || 0 } );
		const material = createMaterial( materialNodes.vertexNode, materialNodes.fragmentNode );

		super( geometry, material );


		/**
		 * This flag can be used for type testing.
		 *
		 * @type {boolean}
		 * @readonly
		 * @default true
		 */
		this.isGaussianSplat = true;

		this.type = 'GaussianSplat';

		/**
		 * The source splat geometry.
		 *
		 * @type {BufferGeometry}
		 */
		this.splatGeometry = splatGeometry;

		/**
		 * The bounding box of the splats. Can be computed via {@link GaussianSplat#computeBoundingBox}.
		 *
		 * @type {?Box3}
		 * @default null
		 */
		this.boundingBox = null;

		/**
		 * The bounding sphere of the splats. Can be computed via {@link GaussianSplat#computeBoundingSphere}.
		 *
		 * @type {?Sphere}
		 * @default null
		 */
		this.boundingSphere = null;

		/**
		 * Whether to sort automatically in `onBeforeRender`.
		 *
		 * @type {boolean}
		 */
		this.autoSort = autoSort;

		this._buffers = buffers;
		// PATCHED (aos): the cameraSeeThrough uniforms, or null when not asked for.
		// Driven through `setSeeThrough` below rather than written directly, because
		// the three values travel together and a half-updated set fades the wrong
		// volume for a frame.
		this._seeThrough = seeThroughUniforms;
		this._sort = sort;
		this._sortMatrix = uniform( new Matrix4() );
		this._sortDepthRange = uniform( new Vector2( 0, 1 ) );
		this._shRotation = shRotation;
		this._sortInitialized = false;
		this._lastSortDirection = new Vector3();
		this._localCameraPosition = localCameraPosition;
		this._sphericalHarmonicsComputeNode = sphericalHarmonicsComputeNode;
		this._sphericalHarmonicsInitialized = false;
		this._lastSphericalHarmonicsCameraMatrix = new Matrix4();
		this._lastSphericalHarmonicsWorldMatrix = new Matrix4();
		this._sphericalHarmonicsVertexNode = materialNodes.sphericalHarmonicsVertexNode;
		this._precomputedSphericalHarmonicsVertexNode = materialNodes.vertexNode;
		this._positionAttribute = positionAttribute;

		const centerRead = buffers.centerRead;
		const sortMatrix = this._sortMatrix;
		const sortDepthRange = this._sortDepthRange;

		sort.setBinNode( () => {

			const center = centerRead.element( instanceIndex ).xyz.toVar( 'center' );
			const viewCenter = sortMatrix.mul( vec4( center, 1 ) ).xyz.toVar( 'viewCenter' );
			// The camera sits at the origin of view space, so length() IS the radial distance.
			const depth = ( GaussianSplat.sortRadial ?? SPLAT_SORT_RADIAL_DEFAULT
				? viewCenter.length() : viewCenter.z.negate() ).toVar( 'depth' );
			const range = max( sortDepthRange.y.sub( sortDepthRange.x ), 0.0001 ).toVar( 'range' );
			const normalized = depth.sub( sortDepthRange.x ).div( range ).clamp( 0, 1 ).toVar( 'normalized' );
			const bins = binCount();
			const depthBin = uint( normalized.mul( bins - 1 ) ).toVar( 'depthBin' );

			return uint( bins - 1 ).sub( depthBin );

		} );

		this.onBeforeRender = ( renderer, scene, camera ) => {

			const vertexNode = renderer.backend && renderer.backend.isWebGLBackend === true ?
				this._sphericalHarmonicsVertexNode :
				this._precomputedSphericalHarmonicsVertexNode;

			if ( vertexNode !== null && material.vertexNode !== vertexNode ) {

				material.vertexNode = vertexNode;
				material.needsUpdate = true;

			}

			this.updateSphericalHarmonics( renderer, camera );

			if ( this.autoSort === true ) {

				this.updateSort( renderer, camera );

			}

		};

	}

	/**
	 * Updates the view-dependent spherical harmonics colors if the camera or
	 * mesh transform has changed.
	 *
	 * @param {Renderer} renderer - The renderer.
	 * @param {Camera} camera - The camera used for rendering.
	 * @return {boolean} Whether a compute pass was dispatched this call.
	 */
	/**
	 * PATCHED (aos): drive the cameraSeeThrough fade.
	 *
	 * `a` and `b` are the camera and the head IN THIS SPLAT'S LOCAL SPACE — the
	 * frame its centres are stored in. For an environment whose pose is baked into
	 * its splats that is world space and the caller passes world coordinates
	 * unchanged; anything else must inverse-transform first. Stated rather than
	 * inferred, because handing world coordinates to a transformed mesh fades a
	 * volume somewhere else entirely and still looks like a working effect.
	 *
	 * `strength` 0 is an exact no-op, so "off" costs nothing and needs no rebuild.
	 * A splat built without `{ seeThrough: true }` ignores this and says so, rather
	 * than silently accepting values nothing reads.
	 *
	 * @param {{x:number,y:number,z:number}} a - camera, local space
	 * @param {{x:number,y:number,z:number}} b - head, local space
	 * @param {number} radius - fully faded within this distance of the segment
	 * @param {number} fade - width of the ramp beyond `radius`
	 * @param {number} strength - 0 untouched, 1 full fade
	 * @param {number} [fromSlot] - the first slot the fade may touch; the avatar's
	 *   mesh passes the first environment slot so the face is exempt, and a mesh
	 *   that is all environment leaves it at 0.
	 * @returns {boolean} whether this splat carries the uniforms at all
	 */
	/**
	 * PATCHED (aos): the rotation taking a WORLD direction into the frame the
	 * spherical-harmonic coefficients were fitted in.
	 *
	 * Pass the INVERSE of whatever rotation a loader baked into the positions and
	 * covariances — for an orthonormal R that is its transpose. A loader that
	 * bakes nothing leaves this identity and pays one matrix multiply per splat.
	 *
	 * This is the correct place for it and the coefficients are not: they are
	 * quantised bytes, and rotating a band redistributes its norm across
	 * components, so re-encoding can clip. Rotating the direction is exact.
	 *
	 * @param {Matrix3} m - world -> coefficient frame.
	 */
	setShRotation( m ) {

		this._shRotation.value.copy( m );

	}

	setSeeThrough( a, b, radius, fade, strength, fromSlot = 0 ) {

		if ( this._seeThrough === null ) return false;
		this._seeThrough.a.value.set( a.x, a.y, a.z );
		this._seeThrough.b.value.set( b.x, b.y, b.z );
		this._seeThrough.k.value.set( radius, fade, strength );
		this._seeThrough.slot0.value = fromSlot;
		return true;

	}

	updateSphericalHarmonics( renderer, camera ) {

		if ( this._sphericalHarmonicsComputeNode === null ) return false;

		const isWebGLBackend = renderer.backend && renderer.backend.isWebGLBackend === true;

		if ( this._sphericalHarmonicsInitialized === true &&
			camera.matrixWorld.equals( this._lastSphericalHarmonicsCameraMatrix ) &&
			this.matrixWorld.equals( this._lastSphericalHarmonicsWorldMatrix ) &&
			( isWebGLBackend === true || this._buffers.sphericalHarmonicsContributionRead !== undefined ) ) {

			return false;

		}

		if ( isWebGLBackend === true ) {

			enableWebGLBuffers( this._buffers );

		}

		_worldMatrixInverse.copy( this.matrixWorld ).invert();
		this._localCameraPosition.value.setFromMatrixPosition( camera.matrixWorld ).applyMatrix4( _worldMatrixInverse );

		this._lastSphericalHarmonicsCameraMatrix.copy( camera.matrixWorld );
		this._lastSphericalHarmonicsWorldMatrix.copy( this.matrixWorld );
		this._sphericalHarmonicsInitialized = true;

		if ( isWebGLBackend === true ) return false;

		ensureSphericalHarmonicsContributionBuffer( this._buffers );
		renderer.compute( this._sphericalHarmonicsComputeNode );

		return true;

	}

	/**
	 * Computes the bounding box of the splats, updating {@link GaussianSplat#boundingBox}.
	 *
	 * Each splat is expanded by its own extent rather than treated as a point, so the bounds cover
	 * what is drawn.
	 */
	computeBoundingBox() {

		if ( this.boundingBox === null ) this.boundingBox = new Box3();

		this.boundingBox.makeEmpty();

		const positionAttribute = this.splatGeometry.getAttribute( 'position' );
		const covarianceAttribute = this.splatGeometry.getAttribute( 'covariance' );
		const count = positionAttribute.count;

		for ( let i = 0; i < count; i ++ ) {

			const x = positionAttribute.getX( i );
			const y = positionAttribute.getY( i );
			const z = positionAttribute.getZ( i );

			const c00 = covarianceAttribute.getComponent( i, 0 );
			const c11 = covarianceAttribute.getComponent( i, 3 );
			const c22 = covarianceAttribute.getComponent( i, 5 );

			// the radius of the drawn largest extent
			const radius = quadCutoff() * Math.sqrt( Math.max( c00, c11, c22 ) );

			this.boundingBox.expandByPoint( _vector.set( x - radius, y - radius, z - radius ) );
			this.boundingBox.expandByPoint( _vector.set( x + radius, y + radius, z + radius ) );

		}

	}

	/**
	 * Computes the bounding sphere of the splats, updating {@link GaussianSplat#boundingSphere}.
	 *
	 * Each splat is expanded by its own extent rather than treated as a point, so the bounds cover
	 * what is drawn.
	 */
	computeBoundingSphere() {

		if ( this.boundingSphere === null ) this.boundingSphere = new Sphere();

		this.computeBoundingBox();
		this.boundingBox.getBoundingSphere( this.boundingSphere );

		const positionAttribute = this.splatGeometry.getAttribute( 'position' );
		const covarianceAttribute = this.splatGeometry.getAttribute( 'covariance' );
		const count = positionAttribute.count;
		const center = this.boundingSphere.center;

		let maxRadius = 0;

		for ( let i = 0; i < count; i ++ ) {

			const x = positionAttribute.getX( i );
			const y = positionAttribute.getY( i );
			const z = positionAttribute.getZ( i );

			const c00 = covarianceAttribute.getComponent( i, 0 );
			const c11 = covarianceAttribute.getComponent( i, 3 );
			const c22 = covarianceAttribute.getComponent( i, 5 );

			// the radius of the drawn largest extent
			const radius = quadCutoff() * Math.sqrt( Math.max( c00, c11, c22 ) );

			maxRadius = Math.max( maxRadius, center.distanceTo( _vector.set( x, y, z ) ) + radius );

		}

		this.boundingSphere.radius = maxRadius;

	}

	// PATCHED (aos): upstream `GaussianSplat` defines NO dispose, and three lines
	// in this app called `mesh.dispose?.()` believing it did — so every rebuild at
	// a new capacity and every character switch left the previous mesh's quad
	// geometry, its material and its source splat geometry alive.
	//
	// Three things are released here and one deliberately is not. The internal
	// quad `geometry` and the `material` are three's own disposables. The
	// `splatGeometry` is the caller's SOURCE buffers, which this class has already
	// copied into storage buffers at construction — nothing reads it again except
	// `computeBoundingSphere`, and a disposed mesh computes nothing.
	//
	// The storage buffers in `_buffers` are NOT freed: three's backend keys their
	// GPU allocations off the attribute objects in a WeakMap, and the only way to
	// destroy one explicitly (`renderer.attributes.delete`) needs a renderer this
	// mesh has never been handed. Dropping the mesh makes them unreachable, which
	// is what upstream relies on too.
	dispose() {

		this.geometry?.dispose?.();
		this.material?.dispose?.();
		this.splatGeometry?.dispose?.();

		return this;

	}

	/**
	 * Computes intersection points between a casted ray and the splats.
	 *
	 * @param {Raycaster} raycaster - The raycaster.
	 * @param {Array<Object>} intersects - The target array that holds the intersection points.
	 */
	raycast( raycaster, intersects ) {

		const matrixWorld = this.matrixWorld;

		// Checking boundingSphere distance to ray

		if ( this.boundingSphere === null ) this.computeBoundingSphere();

		_sphere.copy( this.boundingSphere );
		_sphere.applyMatrix4( matrixWorld );

		if ( raycaster.ray.intersectsSphere( _sphere ) === false ) return;

		//

		_inverseMatrix.copy( matrixWorld ).invert();
		_ray.copy( raycaster.ray ).applyMatrix4( _inverseMatrix );

		// test with bounding box in local space

		if ( this.boundingBox !== null ) {

			if ( _ray.intersectsBox( this.boundingBox ) === false ) return;

		}

		const positionAttribute = this.splatGeometry.getAttribute( 'position' );
		const covarianceAttribute = this.splatGeometry.getAttribute( 'covariance' );
		const colorAttribute = this.splatGeometry.getAttribute( 'color' );
		const count = positionAttribute.count;

		for ( let i = 0; i < count; i ++ ) {

			computeRayIntersection( positionAttribute, covarianceAttribute, colorAttribute, i, matrixWorld, raycaster, intersects, this );

		}

	}

	/**
	 * Updates the draw order if the camera or mesh orientation has changed enough
	 * to need a new sort.
	 *
	 * @param {Renderer} renderer - The renderer.
	 * @param {Camera} camera - The camera used for rendering.
	 * @return {boolean} Whether a sort was dispatched this call.
	 */
	updateSort( renderer, camera ) {

		this.updateWorldMatrix( true, false );

		const needsSort = this._needsSort( camera );

		if ( this._sortInitialized === false || needsSort === true ) {

			this._updateSortUniforms( camera );

			if ( renderer.backend && renderer.backend.isWebGLBackend === true ) {

				enableWebGLBuffers( this._buffers );
				this._sort.enableWebGLBuffers();
				this._sortCPU();

			} else {

				this._sort.compute( renderer );

			}

			this._sortInitialized = true;
			this._lastSortDirection.copy( _sortDirection );

			return true;

		}

		return false;

	}

	_needsSort( camera ) {

		_modelViewMatrix.multiplyMatrices( camera.matrixWorldInverse, this.matrixWorld );

		const e = _modelViewMatrix.elements;
		_sortDirection.set( e[ 2 ], e[ 6 ], e[ 10 ] ).normalize();

		return _sortDirection.dot( this._lastSortDirection ) < SORT_DIRECTION_THRESHOLD;

	}

	_updateSortUniforms( camera ) {

		this._sortMatrix.value.multiplyMatrices( camera.matrixWorldInverse, this.matrixWorld );

		if ( this.boundingSphere === null ) this.computeBoundingSphere();

		_worldCenter.copy( this.boundingSphere.center ).applyMatrix4( this.matrixWorld );
		_viewCenter.copy( _worldCenter ).applyMatrix4( camera.matrixWorldInverse );

		_worldScale.setFromMatrixScale( this.matrixWorld );

		const radius = this.boundingSphere.radius * Math.max( _worldScale.x, _worldScale.y, _worldScale.z );
		// Must use the same metric as the key, or the bins cover the wrong range.
		const depth = ( GaussianSplat.sortRadial ?? SPLAT_SORT_RADIAL_DEFAULT )
			? _viewCenter.length() : - _viewCenter.z;
		const nearDepth = Math.max( camera.near, depth - radius );
		const farDepth = Math.max( nearDepth + 0.0001, depth + radius );

		_sortDepthRange.set( nearDepth, farDepth );
		this._sortDepthRange.value.copy( _sortDepthRange );

	}

	_sortCPU() {

		const centers = this._positionAttribute.array;
		const matrix = this._sortMatrix.value.elements;
		const nearDepth = this._sortDepthRange.value.x;
		const range = Math.max( this._sortDepthRange.value.y - nearDepth, 0.0001 );
		const bins = binCount();
		const scale = ( bins - 1 ) / range;

		this._sort.computeCPU( ( i ) => {

			const i3 = i * 3;
			const cx = centers[ i3 ], cy = centers[ i3 + 1 ], cz = centers[ i3 + 2 ];
			const vz = matrix[ 2 ] * cx + matrix[ 6 ] * cy + matrix[ 10 ] * cz + matrix[ 14 ];
			let depth;
			if ( GaussianSplat.sortRadial ?? SPLAT_SORT_RADIAL_DEFAULT ) {
				const vx = matrix[ 0 ] * cx + matrix[ 4 ] * cy + matrix[ 8 ] * cz + matrix[ 12 ];
				const vy = matrix[ 1 ] * cx + matrix[ 5 ] * cy + matrix[ 9 ] * cz + matrix[ 13 ];
				depth = Math.sqrt( vx * vx + vy * vy + vz * vz );
			} else {
				depth = - vz;
			}
			const depthBin = Math.min( bins - 1, Math.max( 0, Math.floor( ( depth - nearDepth ) * scale ) ) );

			return bins - 1 - depthBin;

		} );

	}

}

// Intersects the ray with the ellipsoid the splat's covariance describes, which reduces to a
// quadratic in t whose smaller root is the near surface.
function computeRayIntersection( positionAttribute, covarianceAttribute, colorAttribute, index, matrixWorld, raycaster, intersects, object ) {

	// skip faint splats - cheapest possible rejection, a single attribute read
	if ( colorAttribute.getW( index ) < MIN_RAYCAST_OPACITY ) {

		return;

	}

	// the diagonal of the covariance bounds the splat's drawn extent (same radius used by
	// computeBoundingBox/computeBoundingSphere); reject rays that don't pass near the splat
	// at all before doing any of the more expensive matrix work below
	const c00 = covarianceAttribute.getComponent( index, 0 );
	const c11 = covarianceAttribute.getComponent( index, 3 );
	const c22 = covarianceAttribute.getComponent( index, 5 );
	const maxVariance = Math.max( c00, c11, c22 );

	if ( maxVariance <= 0 ) {

		return;

	}

	const center = _vector.fromBufferAttribute( positionAttribute, index );
	const boundingRadius = quadCutoff() * Math.sqrt( maxVariance );

	if ( _ray.distanceSqToPoint( center ) > boundingRadius * boundingRadius ) {

		return;

	}

	// the attribute holds the upper triangle of the symmetric covariance
	const c01 = covarianceAttribute.getComponent( index, 1 );
	const c02 = covarianceAttribute.getComponent( index, 2 );
	const c12 = covarianceAttribute.getComponent( index, 4 );

	// splats are often flat enough to make the covariance singular, so the thinnest axis is floored
	// relative to the widest to keep the quadratic solvable
	const minVariance = maxVariance * COVARIANCE_FLATNESS;

	_covarianceMatrix.set(
		c00 + minVariance, c01, c02,
		c01, c11 + minVariance, c12,
		c02, c12, c22 + minVariance
	);

	const determinant = _covarianceMatrix.determinant();

	if ( determinant <= 0 ) {

		return;

	}

	// inverse( covariance ), applied below to the ray direction and to the origin offset
	_covarianceMatrix.invert();

	_mDirection.copy( _ray.direction ).applyMatrix3( _covarianceMatrix );

	// squared length of the ray direction in the ellipsoid's metric; must be positive for a valid covariance
	const a = _ray.direction.dot( _mDirection );

	if ( a <= 0 ) {

		return;

	}

	_originOffset.copy( _ray.origin ).sub( center );
	_mOriginOffset.copy( _originOffset ).applyMatrix3( _covarianceMatrix );

	const b = 2 * _originOffset.dot( _mDirection );
	const c = _originOffset.dot( _mOriginOffset ) - (GaussianSplat.kernelCutoff || SPLAT_KERNEL_CUTOFF_DEFAULT) * (GaussianSplat.kernelCutoff || SPLAT_KERNEL_CUTOFF_DEFAULT);
	const discriminant = b * b - 4 * a * c;

	if ( discriminant < 0 ) {

		return;

	}

	const sqrtDiscriminant = Math.sqrt( discriminant );
	let t = ( - b - sqrtDiscriminant ) / ( 2 * a );

	// the near surface is behind the origin when the ray starts inside the splat
	if ( t < 0 ) {

		t = ( - b + sqrtDiscriminant ) / ( 2 * a );

	}

	if ( t < 0 ) {

		return;

	}

	const intersectPoint = new Vector3();
	_ray.at( t, intersectPoint ).applyMatrix4( matrixWorld );

	const distance = raycaster.ray.origin.distanceTo( intersectPoint );
	if ( distance < raycaster.near || distance > raycaster.far ) {

		return;

	}

	intersects.push( {

		distance: distance,
		point: intersectPoint,
		index: index,
		face: null,
		faceIndex: null,
		barycoord: null,
		object: object

	} );

}

function createGeometry( count ) {

	// The quad is in SIGMA units: the vertex stage multiplies positionGeometry by
	// sqrt(lambda), which is one standard deviation in pixels. So this half-extent
	// IS the kernel cutoff, and upstream hardcodes it to 2 sigma while sparkjs and
	// our own raster use sqrt(8) ~ 2.83. It has to agree with the fragment's own
	// r2 test below or the splat is clipped by whichever is tighter.
	const k = quadCutoff();
	const geometry = new InstancedBufferGeometry();
	geometry.setAttribute( 'position', new BufferAttribute( new Float32Array( [
		- k, - k, 0,
		k, - k, 0,
		k, k, 0,
		- k, k, 0
	] ), 3 ) );
	geometry.setIndex( [ 0, 1, 2, 0, 2, 3 ] );
	geometry.instanceCount = count;

	return geometry;

}

function createStorageBuffers( count, centers, covariances, colors, sphericalHarmonics ) {

	const centerData = new Float32Array( count * 4 );
	const covarianceAData = new Float32Array( count * 4 );
	const covarianceBData = new Float32Array( count * 4 );
	const colorData = new Uint32Array( count );
	const sphericalHarmonicsDegree = sphericalHarmonics.degree;

	for ( let i = 0; i < count; i ++ ) {

		const i3 = i * 3;
		const i4 = i * 4;
		const i6 = i * 6;

		centerData[ i4 ] = centers[ i3 ];
		centerData[ i4 + 1 ] = centers[ i3 + 1 ];
		centerData[ i4 + 2 ] = centers[ i3 + 2 ];

		covarianceAData[ i4 ] = covariances[ i6 ];
		covarianceAData[ i4 + 1 ] = covariances[ i6 + 1 ];
		covarianceAData[ i4 + 2 ] = covariances[ i6 + 2 ];
		covarianceAData[ i4 + 3 ] = covariances[ i6 + 3 ];

		covarianceBData[ i4 ] = covariances[ i6 + 4 ];
		covarianceBData[ i4 + 1 ] = covariances[ i6 + 5 ];

		colorData[ i ] = ( colors[ i4 ] |
			colors[ i4 + 1 ] << 8 |
			colors[ i4 + 2 ] << 16 |
			colors[ i4 + 3 ] << 24 ) >>> 0;

	}

	const centerAttribute = new StorageBufferAttribute( centerData, 4 );
	const covarianceAAttribute = new StorageBufferAttribute( covarianceAData, 4 );
	const covarianceBAttribute = new StorageBufferAttribute( covarianceBData, 4 );
	const colorAttribute = new StorageBufferAttribute( colorData, 1 );

	const buffers = {
		count,
		sphericalHarmonicsDegree,
		webGLBuffersEnabled: false,
		centerRead: storage( centerAttribute, 'vec4', count ).toReadOnly(),
		covarianceARead: storage( covarianceAAttribute, 'vec4', count ).toReadOnly(),
		covarianceBRead: storage( covarianceBAttribute, 'vec4', count ).toReadOnly(),
		colorRead: storage( colorAttribute, 'uint', count ).toReadOnly()
	};

	for ( let degree = 1; degree <= sphericalHarmonicsDegree; degree ++ ) {

		const words = SH_BAND_WORDS[ degree ];
		const attribute = new StorageBufferAttribute( sphericalHarmonics[ `sh${ degree }` ], 1 );

		buffers[ `sphericalHarmonics${ degree }Attribute` ] = attribute;
		buffers[ `sphericalHarmonics${ degree }Read` ] = storage( attribute, 'uint', count * words ).toReadOnly();
		buffers[ `sphericalHarmonics${ degree }Words` ] = words;

	}

	return buffers;

}

function ensureSphericalHarmonicsContributionBuffer( buffers ) {

	if ( buffers.sphericalHarmonicsContributionRead !== undefined ) return;

	// WebGPU stores one precomputed SH contribution per splat. The WebGL
	// fallback evaluates SH in the vertex shader because its transform-feedback
	// compute path cannot perform the packed buffer's indexed reads, so allocate
	// this additional buffer lazily only when the WebGPU pre-pass runs.
	const attribute = new StorageBufferAttribute( new Float32Array( buffers.count * 4 ), 4 );

	buffers.sphericalHarmonicsContributionRead = storage( attribute, 'vec4', buffers.count ).toReadOnly();
	buffers.sphericalHarmonicsContributionWrite = storage( attribute, 'vec4', buffers.count );

}

function enableWebGLBuffers( buffers ) {

	if ( buffers.webGLBuffersEnabled === true ) return;

	buffers.centerRead.setPBO( true );
	buffers.covarianceARead.setPBO( true );
	buffers.covarianceBRead.setPBO( true );
	buffers.colorRead.setPBO( true );

	for ( let degree = 1; degree <= buffers.sphericalHarmonicsDegree; degree ++ ) {

		buffers[ `sphericalHarmonics${ degree }Read` ].setPBO( true );

	}

	buffers.webGLBuffersEnabled = true;

}

function unpackSphericalHarmonicsCoefficients( buffer, splatIndex, words, componentCount ) {

	const coefficients = [];
	let remaining = componentCount;

	for ( let word = 0; word < words && remaining > 0; word ++ ) {

		const packed = buffer.element( splatIndex.mul( words ).add( word ) ).toVar();
		const bytesInWord = Math.min( 4, remaining );

		for ( let byteIndex = 0; byteIndex < bytesInWord; byteIndex ++ ) {

			const byte = packed.shiftRight( byteIndex * 8 ).bitAnd( 0xff );
			coefficients.push( float( byte ).sub( 128 ).div( 128 ) );

		}

		remaining -= bytesInWord;

	}

	return coefficients;

}

function assembleSphericalHarmonicsVectors( coefficients, name ) {

	const vectors = [];
	const vectorCount = coefficients.length / 3;

	for ( let i = 0; i < vectorCount; i ++ ) {

		const offset = i * 3;
		vectors.push( vec3(
			coefficients[ offset ],
			coefficients[ offset + 1 ],
			coefficients[ offset + 2 ]
		).toVar( `${ name }${ i }` ) );

	}

	return vectors;

}

function accumulateSphericalHarmonics( vectors, weights ) {

	let result = vectors[ 0 ].mul( weights[ 0 ] );

	for ( let i = 1; i < vectors.length; i ++ ) {

		result = result.add( vectors[ i ].mul( weights[ i ] ) );

	}

	return result;

}

function applySphericalHarmonicsBand( buffer, splatIndex, words, componentCount, name, weights ) {

	const coefficients = unpackSphericalHarmonicsCoefficients( buffer, splatIndex, words, componentCount );
	const vectors = assembleSphericalHarmonicsVectors( coefficients, name );

	return accumulateSphericalHarmonics( vectors, weights );

}

function applySphericalHarmonics( rgb, center, localCameraPosition, splatIndex, buffers, shRotation ) {

	// PATCHED (aos): the direction is taken into the frame the COEFFICIENTS are
	// in, which is not always the geometry's.
	//
	// Spherical harmonics are DIRECTIONAL, so they are only valid in the frame
	// they were fitted in. A loader that bakes a pose into position and covariance
	// and leaves the coefficients alone (which is what `loadEnvironmentSplats`
	// does, and what it has to do — the avatar and the room share one mesh, and a
	// mesh carries one matrix) leaves the two in DIFFERENT frames, and the
	// view-dependent term is then evaluated against the wrong axes: the DC colour
	// stays right, odd bands change sign under a 180 degree pose and even ones do
	// not, and edges pick up a colour fringe. Measured on Startup_Level, whose
	// pose IS a 180 degree flip, that cost 3.44 against 0.89 mean |RGB| from the
	// Spark reference.
	//
	// ROTATING THE DIRECTION IS EXACT AND ROTATING THE COEFFICIENTS IS NOT. For a
	// bake by R the two are equivalent — evaluating a rotated basis against d is
	// the same as evaluating the original basis against R^T d — but the
	// coefficients are stored as QUANTISED BYTES, and a rotation redistributes a
	// band's norm across its components, so a re-quantised coefficient can clip
	// the encoding's [-1, 1]. This is one 3x3 on a unit vector, per splat, with no
	// re-encode and nothing to lose. (The SPZ reference rotates the coefficients
	// instead — `rotFlipShFuncs` / `flipSh` — because it does so at PACK time,
	// before quantising, where that objection does not apply.)
	//
	// Identity by default, so a loader that does not bake pays one matrix multiply
	// and changes nothing.
	const viewDirection = normalize( shRotation.mul( center.sub( localCameraPosition ) ) ).toVar( 'sphericalHarmonicsViewDirection' );
	const x = viewDirection.x;
	const y = viewDirection.y;
	const z = viewDirection.z;

	rgb.addAssign( applySphericalHarmonicsBand(
		buffers.sphericalHarmonics1Read,
		splatIndex,
		buffers.sphericalHarmonics1Words,
		SH_BAND_COMPONENTS[ 1 ],
		'sh1_',
		[
			y.mul( - 0.4886025 ),
			z.mul( 0.4886025 ),
			x.mul( - 0.4886025 )
		]
	) );

	if ( buffers.sphericalHarmonicsDegree >= 2 ) {

		const xx = x.mul( x ).toVar( 'shXX' );
		const yy = y.mul( y ).toVar( 'shYY' );
		const zz = z.mul( z ).toVar( 'shZZ' );

		rgb.addAssign( applySphericalHarmonicsBand(
			buffers.sphericalHarmonics2Read,
			splatIndex,
			buffers.sphericalHarmonics2Words,
			SH_BAND_COMPONENTS[ 2 ],
			'sh2_',
			[
				x.mul( y ).mul( 1.0925484 ),
				y.mul( z ).mul( - 1.0925484 ),
				zz.mul( 2 ).sub( xx ).sub( yy ).mul( 0.3153915 ),
				x.mul( z ).mul( - 1.0925484 ),
				xx.sub( yy ).mul( 0.5462742 )
			]
		) );

		if ( buffers.sphericalHarmonicsDegree >= 3 ) {

			const xy = x.mul( y ).toVar( 'shXY' );

			rgb.addAssign( applySphericalHarmonicsBand(
				buffers.sphericalHarmonics3Read,
				splatIndex,
				buffers.sphericalHarmonics3Words,
				SH_BAND_COMPONENTS[ 3 ],
				'sh3_',
				[
					y.mul( xx.mul( 3 ).sub( yy ) ).mul( - 0.5900436 ),
					xy.mul( z ).mul( 2.8906114 ),
					y.mul( zz.mul( 4 ).sub( xx ).sub( yy ) ).mul( - 0.4570458 ),
					z.mul( zz.mul( 2 ).sub( xx.mul( 3 ) ).sub( yy.mul( 3 ) ) ).mul( 0.3731763 ),
					x.mul( zz.mul( 4 ).sub( xx ).sub( yy ) ).mul( - 0.4570458 ),
					z.mul( xx.sub( yy ) ).mul( 1.4453057 ),
					x.mul( xx.sub( yy.mul( 3 ) ) ).mul( - 0.5900436 )
				]
			) );

		}

	}

}

function createSphericalHarmonicsComputeNode( buffers, localCameraPosition, shRotation ) {

	if ( buffers.sphericalHarmonicsDegree === 0 ) return null;

	return Fn( () => {

		const splatIndex = instanceIndex;
		const center = buffers.centerRead.element( splatIndex ).xyz.toVar( 'center' );
		const rgb = vec3( 0 ).toVar( 'sphericalHarmonicsContribution' );

		applySphericalHarmonics( rgb, center, localCameraPosition, splatIndex, buffers, shRotation );
		buffers.sphericalHarmonicsContributionWrite.element( splatIndex ).assign( vec4( rgb, 0 ) );

	} )().compute( buffers.count, [ WORKGROUP_SIZE ] ).setName( 'GaussianSplatSphericalHarmonics' );

}

function createMaterialNodes( buffers, sort, localCameraPosition, shRotation, seeThroughUniforms = null,
	// PATCHED (aos): this splat's own blur/alpha settings. See the constructor.
	blur = { alphaAttenuation: false, alphaAttenuationFloor: 0 } ) {

	const splatUv = varyingProperty( 'vec2', 'vSplatUv' );
	const splatColor = varyingProperty( 'vec4', 'vSplatColor' );

	const createVertexNode = ( usePrecomputedSphericalHarmonics ) => Fn( () => {

		const splatIndex = sort.orderRead.element( instanceIndex ).toVar( 'splatIndex' );
		const center = buffers.centerRead.element( splatIndex ).xyz.toVar( 'center' );
		const covA = buffers.covarianceARead.element( splatIndex ).toVar( 'covA' );
		const covB = buffers.covarianceBRead.element( splatIndex ).toVar( 'covB' );
		const color = unpackUnorm4x8( buffers.colorRead.element( splatIndex ) ).toVar( 'splatColor' );
		const rgb = color.rgb.toVar( 'splatRgb' );

		if ( buffers.sphericalHarmonicsDegree > 0 ) {

			if ( usePrecomputedSphericalHarmonics === true ) {

				rgb.addAssign( buffers.sphericalHarmonicsContributionRead.element( splatIndex ).rgb );

			} else {

				applySphericalHarmonics( rgb, center, localCameraPosition, splatIndex, buffers, shRotation );

			}

		}

		splatUv.assign( positionGeometry.xy );

		const viewCenter4 = highpModelViewMatrix.mul( vec4( center, 1 ) ).toVar( 'viewCenter4' );
		const viewCenter = viewCenter4.xyz.toVar( 'viewCenter' );
		const centerClip = cameraProjectionMatrix.mul( viewCenter4 ).toVar( 'centerClip' );

		const m = highpModelViewMatrix;
		const r0 = vec3( m[ 0 ].x, m[ 1 ].x, m[ 2 ].x ).toVar( 'r0' );
		const r1 = vec3( m[ 0 ].y, m[ 1 ].y, m[ 2 ].y ).toVar( 'r1' );
		const r2 = vec3( m[ 0 ].z, m[ 1 ].z, m[ 2 ].z ).toVar( 'r2' );

		const cov0 = vec3( covA.x, covA.y, covA.z ).toVar( 'cov0' );
		const cov1 = vec3( covA.y, covA.w, covB.x ).toVar( 'cov1' );
		const cov2 = vec3( covA.z, covB.x, covB.y ).toVar( 'cov2' );

		const vc0 = vec3( dot( r0, cov0 ), dot( r0, cov1 ), dot( r0, cov2 ) ).toVar( 'vc0' );
		const vc1 = vec3( dot( r1, cov0 ), dot( r1, cov1 ), dot( r1, cov2 ) ).toVar( 'vc1' );
		const vc2 = vec3( dot( r2, cov0 ), dot( r2, cov1 ), dot( r2, cov2 ) ).toVar( 'vc2' );

		const c00 = dot( vc0, r0 ).toVar( 'c00' );
		const c01 = dot( vc0, r1 ).toVar( 'c01' );
		const c02 = dot( vc0, r2 ).toVar( 'c02' );
		const c11 = dot( vc1, r1 ).toVar( 'c11' );
		const c12 = dot( vc1, r2 ).toVar( 'c12' );
		const c22 = dot( vc2, r2 ).toVar( 'c22' );

		const z = min( viewCenter.z, - 0.01 ).toVar( 'z' );
		const invZ = float( 1 ).div( z ).toVar( 'invZ' );
		const invZ2 = invZ.mul( invZ ).toVar( 'invZ2' );
		const focal = cameraViewport.zw.mul( 0.5 ).mul( vec2( cameraProjectionMatrix[ 0 ].x, cameraProjectionMatrix[ 1 ].y ) ).toVar( 'focal' );

		const j00 = focal.x.negate().mul( invZ ).toVar( 'j00' );
		const j11 = focal.y.negate().mul( invZ ).toVar( 'j11' );
		const j02 = focal.x.mul( viewCenter.x ).mul( invZ2 ).toVar( 'j02' );
		const j12 = focal.y.mul( viewCenter.y ).mul( invZ2 ).toVar( 'j12' );

		const aBase = j00.mul( j00 ).mul( c00 )
			.add( j00.mul( j02 ).mul( c02 ).mul( 2 ) )
			.add( j02.mul( j02 ).mul( c22 ) )
			.toVar( 'cov2dABase' );
		const b = j00.mul( j11 ).mul( c01 )
			.add( j00.mul( j12 ).mul( c02 ) )
			.add( j02.mul( j11 ).mul( c12 ) )
			.add( j02.mul( j12 ).mul( c22 ) )
			.toVar( 'cov2dB' );
		const cBase = j11.mul( j11 ).mul( c11 )
			.add( j11.mul( j12 ).mul( c12 ).mul( 2 ) )
			.add( j12.mul( j12 ).mul( c22 ) )
			.toVar( 'cov2dCBase' );
		const a = aBase.add( KERNEL_2D_SIZE ).toVar( 'cov2dA' );
		const c = cBase.add( KERNEL_2D_SIZE ).toVar( 'cov2dC' );
		const detBase = aBase.mul( cBase ).sub( b.mul( b ) ).toVar( 'detBase' );
		const det = a.mul( c ).sub( b.mul( b ) ).toVar( 'det' );
		// PATCHED (aos): the sparkjs-style alpha attenuation is OPT-IN here.
		// Upstream multiplies every splat's alpha by sqrt(detBase/det) to conserve
		// the +KERNEL_2D_SIZE blur's integral. The rasterizer the model was FIT
		// AGAINST applies the same blur and does NOT touch alpha, so the attenuation
		// erases sub-pixel splats -- measured on isaac-v8 it costs ~15% of the
		// rendered detail energy, which is what reads as a soft, waxy face. Zeroing
		// KERNEL_2D_SIZE would also remove the dilation, which we keep.
		// PATCHED (aos): ...and here is the switch the note above promised. It did
		// not exist: `alphaScale` was pinned to 1, so "opt-in" described an option
		// nobody could take and the attenuation could not be measured at all.
		//
		// It matters because the two consumers want OPPOSITE answers. An avatar's
		// decoder was fit against a rasterizer that applies the blur and does NOT
		// touch alpha, so attenuating erases its sub-pixel splats. An ENVIRONMENT
		// is a scan replayed as-is, and both upstream three and sparkjs attenuate
		// it — so a room drawn without it composites differently from the render it
		// replaced. Hence a per-INSTANCE option rather than a constant: the two can
		// be alive in the same frame, so a single shared value cannot serve both.
		// PATCHED (aos): ...with a FLOOR, because the attenuation's failure mode is
		// specific and it is not softness.
		//
		// `sqrt(detBase/det)` conserves the +KERNEL_2D_SIZE blur's integral, and for
		// a SMALL splat `detBase << det`, so the factor goes to ZERO: the dilation
		// keeps the splat on screen and the attenuation takes all its ink away.
		// Measured on isaac-v8 the median factor is 0.000 on a 512 atlas — teeth and
		// eyeballs rendered out of existence — and on Startup_Level it is thin
		// features that go: the tile grout fades to nothing while the flat tile
		// faces get visibly smoother. So the term is doing two jobs, one wanted
		// (large overlapping splats composite more evenly) and one not.
		//
		// `alphaAttenuationFloor` keeps the first and bounds the second: 0 is the
		// unmodified term, 1 disables it entirely, and in between is how much of a
		// sub-pixel splat's ink survives. It is a separate knob from the on/off
		// because "should the blur conserve energy" and "may a splat be erased" are
		// different questions and only the second one destroys detail.
		const alphaScale = ( blur.alphaAttenuation === true
			? sqrt( detBase.div( det.max( 1e-9 ) ) ).max( blur.alphaAttenuationFloor )
			: float( 1 ) ).toVar( 'alphaScale' );

		// PATCHED (aos): cameraSeeThrough. Fade splats lying between the camera and
		// the avatar's head so the room does not occlude the face while orbiting.
		//
		// On the Spark path this was a `worldModifier` — a per-Gaussian dyno spliced
		// into its shader. There is no such hook here, so it lives where the centre
		// is already in hand: four uniforms, no dispatch.
		//
		// THE AVATAR'S MESH USES THIS TOO, and it could not until `slot0` existed.
		// That mesh holds the face and the room in ONE buffer, so a fade centred on
		// the head would dissolve the face it exists to reveal — which is why the
		// bridge ran the identical maths as a COMPUTE pass over the tail's slot
		// range instead, with its own shader, its own pipeline and a full copy of
		// the tail's colours to read back from. `splatIndex` is the slot, right
		// here, so the distinction the shader "could not make" was one `step` away.
		//
		// The formula: distance to the camera->head SEGMENT, smoothstepped over
		// [radius, radius + fade], blended by `strength` (auto mode ramps that).
		// `strength` 0 is an exact no-op — `mix( 1, f, 0 )` is 1 — so an undriven
		// splat is bit-identical to one built without this.
		if ( seeThroughUniforms !== null ) {

			const stA = seeThroughUniforms.a;
			const stB = seeThroughUniforms.b;
			const stAB = stB.sub( stA ).toVar( 'seeThroughAB' );
			const stT = dot( center.sub( stA ), stAB )
				.div( max( dot( stAB, stAB ), float( 1e-6 ) ) ).clamp( 0, 1 ).toVar( 'seeThroughT' );
			const stD = center.sub( stA.add( stAB.mul( stT ) ) ).length().toVar( 'seeThroughD' );
			const radius = seeThroughUniforms.k.x;
			// FUNCTION form, not the method chain. TSL puts the receiver FIRST, so
			// `d.smoothstep( e0, e1 )` is `smoothstep( d, e0, e1 )` — the distance
			// read as edge0. That compiles, runs, and returns 1 for everything, so
			// the effect is simply absent with no error anywhere; it cost a
			// bisection down to forcing the alpha to zero inside this very block to
			// prove the block was even reached.
			const f = smoothstep( radius, radius.add( seeThroughUniforms.k.y ), stD ).toVar( 'seeThroughF' );
			// SLOT GATE, as `step` rather than a branch: 1 for a splat at or past
			// `slot0`, 0 before it, folded into the strength so an exempt splat takes
			// `mix( 1, f, 0 )` — exactly 1, the same no-op an undriven mesh gets.
			const inRange = step( seeThroughUniforms.slot0, float( splatIndex ) ).toVar( 'seeThroughSlotGate' );
			const fade = mix( float( 1 ), f, seeThroughUniforms.k.z.mul( inRange ) ).toVar( 'seeThroughFade' );
			alphaScale.mulAssign( fade );

		}

		// PATCHED (aos): A SPLAT'S COLOUR IS DISPLAY-REFERRED, so it has to be taken
		// into the renderer's working (linear) space here or the output pass encodes
		// it a SECOND time.
		//
		// Upstream writes the unpacked bytes straight out, which is right only when
		// the renderer does not encode — and three encodes linear->sRGB by default.
		// sparkjs never hit this because its splat shader is a raw ShaderMaterial
		// that includes neither `<colorspace_fragment>` nor `<tonemapping_fragment>`,
		// so its colours reached the framebuffer untouched. Measured against that
		// reference on Startup_Level: the decoded data is BIT-IDENTICAL between the
		// two loaders (mean bytes 46.65/46.61/47.71, same histogram), while the
		// rendered wall came out 73-85 levels brighter — `sRGB(33/255)` is 103 and
		// the measurement was 105.
		//
		// `ThreeSplatBridge` used to compensate for the same thing by forcing its own
		// renderer to `LinearSRGBColorSpace` ("brightened the whole face, mean luma
		// 153 -> 193"). That override is gone with this: a renderer-wide setting
		// cannot work on the path where the splats share a canvas with the avatar,
		// whose PBR materials genuinely do need the encode.
		//
		// THE TRANSFER FUNCTION DIRECTLY, not `colorSpaceToWorking`. That helper
		// returns a VEC4 — `vec4( sRGBTransferEOTF( node.rgb ), node.a )` — so
		// `vec4( colorSpaceToWorking( rgb ), alpha )` fills from the node's own four
		// components and DISCARDS the alpha argument, taking `alphaScale` with it.
		// Nothing errors: the splats render, at the right colour, and the
		// see-through fade above is silently gone. Bisected to exactly this line
		// after the environment stopped fading (6559 lit pixels before the fade and
		// 6559 after, with every one of them proven to be environment).
		// `sRGBTransferEOTF` is the same transfer the helper applies, and it leaves
		// the vec3 a vec3. The primaries half of the helper is a no-op here: the
		// working space is linear-sRGB, the same primaries these colours already use.
		splatColor.assign( vec4( sRGBTransferEOTF( rgb.clamp( 0, 1 ) ), color.a.mul( alphaScale ) ) );

		const halfTrace = a.add( c ).mul( 0.5 ).toVar( 'halfTrace' );
		const radius = sqrt( max( a.sub( c ).mul( 0.5 ).pow2().add( b.mul( b ) ), 0.0000001 ) ).toVar( 'radius' );
		const lambda1 = max( halfTrace.add( radius ), 0.0000001 ).toVar( 'lambda1' );
		const lambda2 = max( halfTrace.sub( radius ), 0.0000001 ).toVar( 'lambda2' );
		const axis1 = vec2( 1, 0 ).toVar( 'axis1' );

		If( radius.greaterThan( 0.00001 ), () => {

			const angle = atan( b.mul( 2 ), a.sub( c ) ).mul( 0.5 ).toVar( 'angle' );
			axis1.assign( vec2( cos( angle ), sin( angle ) ) );

		} );

		const axis2 = vec2( axis1.y.negate(), axis1.x ).toVar( 'axis2' );

		const scale1 = min( sqrt( lambda1 ), MAX_SCREEN_SPACE_SPLAT_SIZE ).toVar( 'scale1' );
		const scale2 = min( sqrt( lambda2 ), MAX_SCREEN_SPACE_SPLAT_SIZE ).toVar( 'scale2' );
		const offsetPixels = axis1.mul( positionGeometry.x ).mul( scale1 ).add( axis2.mul( positionGeometry.y ).mul( scale2 ) ).toVar( 'offsetPixels' );
		const offsetNdc = offsetPixels.mul( 2 ).div( cameraViewport.zw ).toVar( 'offsetNdc' );
		const clip = centerClip.add( vec4( offsetNdc.mul( centerClip.w ), 0, 0 ) ).toVar( 'clip' );

		const clipLimit = centerClip.w.mul( CLIP_XY ).toVar( 'clipLimit' );

		If( viewCenter.z.greaterThanEqual( - 0.01 )
			.or( centerClip.z.lessThan( centerClip.w.negate() ) )
			.or( centerClip.z.greaterThan( centerClip.w ) )
			.or( centerClip.x.lessThan( clipLimit.negate() ) )
			.or( centerClip.x.greaterThan( clipLimit ) )
			.or( centerClip.y.lessThan( clipLimit.negate() ) )
			.or( centerClip.y.greaterThan( clipLimit ) ), () => {

			clip.assign( vec4( 2, 2, 2, 1 ) );

		} );

		return clip;

	} )();

	const vertexNode = createVertexNode( true );
	const sphericalHarmonicsVertexNode = buffers.sphericalHarmonicsDegree > 0 ? createVertexNode( false ) : null;

	const fragmentNode = Fn( () => {

		const r2 = dot( splatUv, splatUv ).toVar( 'r2' );

		// Squared kernel cutoff, matching createGeometry's quad half-extent.
		const kernelCutoff2 = ( GaussianSplat.kernelCutoff || SPLAT_KERNEL_CUTOFF_DEFAULT ) ** 2;

		If( r2.greaterThan( kernelCutoff2 ), () => {

			Discard();

		} );

		const gaussian = exp( r2.mul( - 0.5 ) ).toVar( 'gaussian' );
		const splatAlpha = gaussian.mul( splatColor.a.min( 1 ) ).toVar( 'splatAlpha' );

		return vec4( splatColor.rgb, splatAlpha );

	} )();

	return { vertexNode, sphericalHarmonicsVertexNode, fragmentNode };

}

function createMaterial( vertexNode, fragmentNode ) {

	const material = new NodeMaterial();
	material.vertexNode = vertexNode;
	// THE ROOM CANNOT LEAVE THE TONE CURVE FROM HERE, and both ways out were
	// tried. `three.webgpu.js` contains ZERO references to `toneMapped`, so the
	// node renderer has no per-material opt-out — setting it changes nothing,
	// measured. And assigning `material.fragmentNode` instead of `colorNode`,
	// which DOES short-circuit the output stage for a hand-built node graph,
	// makes no difference for this material either: with the transfer unchanged
	// the two render identically (wall mean 32.7 against 32.8). Only the
	// renderer-wide `toneMapping` reaches it, which is not a per-object answer.
	//
	// It matters because sparkjs never went through this stage at all — its raw
	// GLSL includes neither `<tonemapping_fragment>` nor `<colorspace_fragment>` —
	// so main's room was never tone-mapped and this one is. That single difference
	// accounts for the room's levels and about half the dark wall's lost structure.
	material.colorNode = fragmentNode;
	material.transparent = true;
	material.depthWrite = false;
	material.depthTest = true;
	material.forceSinglePass = true;
	material.fog = false;

	return material;

}

// PATCHED (aos): these two are the DEFAULT for a splat that names neither; each
// instance latches its own at construction (see the constructor). Default OFF so
// the render matches diff_gaussian_rasterization,
// which is what these checkpoints were trained against. Set true to restore
// upstream's sparkjs-compatible behaviour — which is what an environment SCAN
// wants, since Spark drew it that way.
GaussianSplat.alphaAttenuation = false;
// PATCHED (aos): see the kernel-cutoff note above.
GaussianSplat.kernelCutoff = SPLAT_KERNEL_CUTOFF_DEFAULT;
GaussianSplat.sortRadial = SPLAT_SORT_RADIAL_DEFAULT;
// PATCHED (aos): see the alphaScale note. 0 is upstream's unmodified attenuation.
GaussianSplat.alphaAttenuationFloor = 0;

export { GaussianSplat };
